import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLazyGetAcomInfoQuery } from '../../apiRtk/be4feApi';
import { SERVICE } from '../../config/devices';
import { SITE_CATEGORY } from '../../config/sites';
import { isActivable, isExpirable } from '../../helpers/mobileVideo';
import { useGetActualCompanyId } from '../../hooks/companyHooks';
import { useAcomConnect } from '../../hooks/useAcomConnect';
import { useModal } from '../../hooks/useModal';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError } from '../../modules/notifications/actions';
import { ISite } from '../../modules/sites/store';
import acomCardMessages from '../../pages/SitePages/SiteDashboardPage/AccessCommanderLoginCard/messages';
import AccessCommanderConnectingModal from '../AccessCommanderConnectingModal';
import { ChangeSiteTypeModal as ChangeSiteTypeDialog } from '../SiteModal/ChangeSiteTypeModal';
import sitesActions from './../../modules/sites';
import RowMenu from './../DataGrid/RowMenu';
import RowMenuItem from './../DataGrid/RowMenuItem';
import messages from './messages';

export function SitesDataGridRowMenu({ site }: { site: ISite }) {
  const dispatch = useDispatch();
  const companyId = useGetActualCompanyId();
  const [getAcomInfo] = useLazyGetAcomInfoQuery();
  const { isConnecting, openAccessCommander } = useAcomConnect();
  const isAcomSite = site?.siteCategory === SITE_CATEGORY.ACCESS_COMMANDER_REMOTE;
  const isNotActivableOrExpirable =
    !isActivable(site?.services?.[SERVICE.MOBILE_VIDEO]?.licenceModel?.status) &&
    !isExpirable(site?.services?.[SERVICE.MOBILE_VIDEO]?.licenceModel?.status);

  const { Modal: ChangeSiteTypeModal, onOpen: openChangeSiteTypeModal } = useModal({
    Modal: ChangeSiteTypeDialog,
  });

  async function handleOpenAccessCommander() {
    if (companyId === undefined || site?.id === undefined) {
      return;
    }
    const acomInfo = await getAcomInfo({ companyId, siteId: site.id })
      .unwrap()
      .catch(() => undefined);
    if (acomInfo?.connectionStatus !== 'CONNECTED') {
      dispatch(addSnackbarError(acomCardMessages.AccessCommanderRemoteCardAlertDisconnectedTitle, TYPE_DESCRIPTOR));
      return;
    }
    openAccessCommander({ companyId, connectionUrl: acomInfo.connectionUrl, siteId: site.id });
  }

  if (isAcomSite) {
    return (
      <>
        <RowMenu
          dataTestId="sites-row-menu"
          menuItems={
            <Box>
              <RowMenuItem
                dataTestId="sites-rename-site"
                icon={<EditIcon />}
                label={<FormattedMessage {...messages.SitesDataGridActionsRename} />}
                onClick={() => dispatch(sitesActions.editSiteShowModal(site))}
              />
              <RowMenuItem
                dataTestId="sites-open-access-commander"
                icon={<OpenInNewIcon />}
                label={<FormattedMessage {...messages.SitesDataGridActionsOpenAccessCommander} />}
                onClick={handleOpenAccessCommander}
              />
              <Divider />
              <RowMenuItem
                colorIcon="error.dark"
                colorText="error.dark"
                icon={<DeleteIcon />}
                label={<FormattedMessage {...messages.sitesDataGridActionsDelete} />}
                onClick={() => dispatch(sitesActions.deleteSiteShowModal(site))}
              />
            </Box>
          }
        />
        <AccessCommanderConnectingModal open={isConnecting} />
      </>
    );
  }

  return (
    <>
      <RowMenu
        dataTestId="sites-row-menu"
        menuItems={
          <Box>
            <RowMenuItem
              dataTestId="sites-rename-site"
              icon={<EditIcon />}
              label={<FormattedMessage {...messages.SitesDataGridActionsRename} />}
              onClick={() => dispatch(sitesActions.editSiteShowModal(site))}
            />
            {isNotActivableOrExpirable && (
              <RowMenuItem
                dataTestId="sites-change-site-type"
                icon={<LocationOnIcon />}
                label={<FormattedMessage {...messages.sitesDataGridActionsChangeSiteType} />}
                onClick={openChangeSiteTypeModal}
              />
            )}
            <Divider />
            <RowMenuItem
              colorIcon="error.dark"
              colorText="error.dark"
              icon={<DeleteIcon />}
              key="deleteSite"
              label={<FormattedMessage {...messages.sitesDataGridActionsDelete} />}
              onClick={() => dispatch(sitesActions.deleteSiteShowModal(site))}
            />
          </Box>
        }
      />
      <ChangeSiteTypeModal siteId={site?.id} />
    </>
  );
}
